import { memo, useMemo, useState } from "react";
import { VisibilityState } from "@tanstack/table-core";
import { Button } from "../../../../Button";
import { ContentDivider } from "../../../../ContentDivider";
import { SimpleSelect, Toggle } from "../../../../inputs";
import { FeaturedIcon } from "../../../../MiscIcons";
import { ProgressButton } from "../../../../ProgressButton";
import { SectionFooter } from "../../../../SectionFooter";
import { SideoutMenu, SideoutMenuHeader } from "../../../../SideoutMenu";
import { useTableContext, useTablePropsContext } from "../../../context";
import { useSettingsContext } from "../SettingsContext";
import { ColumnsSettings } from "./ColumnsSettings";

import { ReactComponent as SettingsIcon } from "../icons/settings.svg";

import styles from "./styles.module.scss";

export const SettingsSidebar = memo(() => {
  const [ keyPrefix, setKeyPrefix ] = useState(Date.now);
  const {
    isOpen, setIsOpen,
    advancedFilters: tableAdvancedFilters, setAdvancedFilters: tableSetAdvancedFilters,
    rowView: tableRowView, setRowView: tableSetRowView,
  } = useSettingsContext();
  const table = useTableContext();
  const tableProps = useTablePropsContext();

  const [ rowsPerPage, setRowsPerPage ] = useState<number | undefined>(table?.getState().pagination.pageSize);
  const [ advancedFilters, setAdvancedFilters ] = useState(tableAdvancedFilters ?? tableProps?.defaultUseAdvancedFilters ?? false);
  const [ rowView, setRowView ] = useState(tableRowView ?? tableProps?.defaultRowView ?? "regular");
  const [ columnVisibility, setColumnVisibility ] = useState<VisibilityState>(table?.getState?.().columnVisibility!);
  const [ columnOrder, setColumnOrder ] = useState<string[]>(table?.getState?.().columnOrder!);

  const allLeafColumns = table!.getAllLeafColumns();
  const filtersAvailable = useMemo(
    () => !!allLeafColumns.filter(c => c.getCanFilter()).length,
    [ allLeafColumns ],
  );

  const applySettings = () => {
    table?.setPagination(old => {
      const newPageSize = rowsPerPage || tableProps?.defaultPageSize!;
      if (old.pageSize === newPageSize) {
        return old;
      }
      return ({
        pageSize: newPageSize,
        pageIndex: 0,
      });
    });

    tableSetRowView(rowView);
    tableSetAdvancedFilters(advancedFilters);
    if (!advancedFilters && tableAdvancedFilters) {
      table?.resetColumnFilters();
    }
    table?.setColumnVisibility(columnVisibility);
    table?.setColumnOrder(columnOrder);
  };

  const restoreSettings = () => {
    setRowsPerPage(table?.getState().pagination.pageSize);
    setAdvancedFilters(tableAdvancedFilters ?? tableProps?.defaultUseAdvancedFilters ?? false);
    setRowView(tableRowView ?? tableProps?.defaultRowView ?? "regular");
    setColumnVisibility(table?.getState?.().columnVisibility!);
    setColumnOrder(table?.getState?.().columnOrder!);
  };

  const resetSettings = async () => {
    setKeyPrefix(Date.now());

    setRowsPerPage(table?.initialState?.pagination?.pageSize!);
    setAdvancedFilters(tableProps?.defaultUseAdvancedFilters || false);
    setRowView(tableProps?.defaultRowView ?? "regular");
    setColumnVisibility(table?.initialState?.columnVisibility!);
    setColumnOrder((() => {
      const declaredColumnOrder = table?.getAllFlatColumns()
        .filter(x => x.accessorFn)
        .map(x => x.id) || [];
      const initialColumnOrder = table?.initialState?.columnOrder || [];

      return [
        ...initialColumnOrder.filter(x => declaredColumnOrder?.indexOf(x) !== -1),
        ...declaredColumnOrder.filter(x => initialColumnOrder?.indexOf(x) === -1),
      ];
    })());
  };

  const onRequestClose = () => {
    restoreSettings();
    setIsOpen(false);
  };

  if (tableProps?.hideSettings) {
    return null;
  }

  return (
    <SideoutMenu
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      renderType="block"
      overlayProps={{
        backgroundType: "gradient",
      }}
      scrollable
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      className={styles.sidebar}
      sidebarContainerClassName={styles.sidebarContainer}
      header={
        <SideoutMenuHeader
          text="Table settings"
          supportingText="You can adjust columns visibility and order"
          onRequestClose={onRequestClose}
          icon={<FeaturedIcon size="md" type="modern"><SettingsIcon /></FeaturedIcon>}
        />
      }
      footer={
        <SectionFooter
          divider
          children={
            <ProgressButton
              buttonSize="md"
              buttonType="tertiaryColor"
              onClick={resetSettings}
            >
              Reset
            </ProgressButton>
          }
          actions={
            <>
              <Button
                buttonSize="md"
                buttonType="secondaryGray"
                onClick={onRequestClose}
              >
                Cancel
              </Button>
              <Button
                buttonSize="md"
                buttonType="primary"
                onClick={() => {
                  applySettings();
                  setIsOpen(false);
                }}
              >
                Save
              </Button>
            </>
          }
        />
      }>
      <div className={styles.content}>
        <div className={styles.block}>
          {
            tableProps?.displayStrategy !== "all" &&
            <SimpleSelect<number, false>
              key="rowsPerPage"
              label="Rows per page"
              options={tableProps?.pageOptions}
              value={rowsPerPage}
              onChange={newValue => setRowsPerPage(newValue ?? undefined)}
            />
          }
          <Toggle
            toggleSize="sm"
            text="Compact row view"
            value={rowView === "compact"}
            onChange={value => setRowView(value ? "compact" : "regular")}
          />
          {
            filtersAvailable &&
            <Toggle
              toggleSize="sm"
              text="Advanced filters"
              value={advancedFilters}
              onChange={setAdvancedFilters}
            />
          }
        </div>
        <ContentDivider contentDividerStyle="singleLine" />
        <ColumnsSettings
          key={`${keyPrefix}_columns`}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
        />
      </div>
    </SideoutMenu>
  );
});